<template>
  <div class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 max-w-lg w-full relative">
      <button
        @click="$emit('close')"
        class="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        aria-label="Close"
      >
        ✕
      </button>

      <h2 class="text-xl font-bold mb-4">
        {{ farm?.id ? 'Edit Farm' : 'Create New Farm' }}
      </h2>

      <form @submit.prevent="submit">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Name -->
          <div>
            <label for="name" class="block font-medium mb-1">Name</label>
            <input
              id="name"
              v-model="form.name"
              type="text"
              required
              class="border rounded px-3 py-2 w-full"
            />
          </div>

          <!-- Location -->
          <div>
            <label for="location" class="block font-medium mb-1">Location</label>
            <input
              id="location"
              v-model="form.location"
              type="text"
              required
              class="border rounded px-3 py-2 w-full"
            />
          </div>

          <!-- Unit -->
          <div>
            <label for="size_unit" class="block font-medium mb-1">Unit</label>
            <select
              id="size_unit"
              v-model="form.size_unit"
              required
              class="border rounded px-3 py-2 w-full"
            >
              <option value="" disabled>Select unit</option>
              <option value="ACRES">Acres</option>
              <option value="HECTARES">Hectares</option>
              <option value="SQUARE_METRES">Square Metres</option>
              <option value="DIMENSIONS">Width × Length</option>
            </select>
          </div>

          <!-- Size -->
          <div>
            <label for="size" class="block font-medium mb-1">
              Size <span v-if="form.size_unit">({{ displayUnit }})</span>
            </label>
            <input
              id="size"
              v-model="form.size"
              :placeholder="sizePlaceholder"
              :disabled="!form.size_unit"
              required
              class="border rounded px-3 py-2 w-full"
            />
          </div>

          <!-- Status -->
          <div>
            <label for="status" class="block font-medium mb-1">Status</label>
            <select
              id="status"
              v-model="form.status"
              required
              class="border rounded px-3 py-2 w-full"
            >
              <option value="" disabled>Select status</option>
              <option value="ACTIVE">Active</option>
              <option value="RENTED_FROM">Rented From Another Person</option>
              <option value="RENTED_TO">Rented To Another Person</option>
              <option value="SOLD">Sold</option>
            </select>
          </div>

          <!-- Rented Seasons -->
          <div>
            <label for="rented_seasons" class="block font-medium mb-1">Rented Seasons</label>
            <input
              id="rented_seasons"
              v-model="form.rented_seasons"
              type="number"
              min="1"
              :disabled="!isRented"
              class="border rounded px-3 py-2 w-full"
            />
          </div>

          <!-- Soil Type -->
          <div>
            <label for="soil_type" class="block font-medium mb-1">Soil Type</label>
            <select
              id="soil_type"
              v-model="form.soil_type"
              required
              class="border rounded px-3 py-2 w-full"
            >
              <option value="" disabled>Select soil type</option>
              <option value="CLAY">Clay</option>
              <option value="LOAM">Loam</option>
              <option value="SANDY">Sandy</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <!-- Description -->
          <div class="sm:col-span-2">
            <label for="description" class="block font-medium mb-1">Description</label>
            <textarea
              id="description"
              v-model="form.description"
              rows="3"
              class="border rounded px-3 py-2 w-full"
            ></textarea>
          </div>
        </div>

        <!-- Buttons -->
        <div class="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            @click="$emit('close')"
            class="py-2 px-4 rounded border border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="py-2 px-6 rounded bg-green-600 hover:bg-green-700 text-white"
          >
            {{ farm?.id ? 'Update' : 'Create' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch } from 'vue'
import { createFarmAPI, updateFarmAPI } from '@/api/farmService'
import { useNotificationStore } from '@/stores/notification'
import type { Farm } from '@/types/Farm'

const props = defineProps<{ farm: Farm | null }>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const notificationStore = useNotificationStore()

// Form as string for size
const form = reactive({
  id: '',
  name: '',
  location: '',
  size: '', // now string
  size_unit: '',
  status: '',
  rented_seasons: 0,
  soil_type: '',
  description: '',
})

watch(
  () => props.farm,
  (newFarm) => {
    if (newFarm) {
      Object.assign(form, newFarm)
    } else {
      form.id = ''
      form.name = ''
      form.location = ''
      form.size = ''
      form.size_unit = ''
      form.status = ''
      form.rented_seasons = 0
      form.soil_type = ''
      form.description = ''
    }
  },
  { immediate: true },
)

const isRented = computed(() => {
  return form.status === 'RENTED_FROM' || form.status === 'RENTED_TO'
})

const submit = async () => {
  try {
    if (form.id) {
      await updateFarmAPI(form.id, form)
      notificationStore.showSuccess('Farm updated successfully!')
    } else {
      await createFarmAPI(form)
      notificationStore.showSuccess('Farm created successfully!')
    }
    emit('saved')
    emit('close')
  } catch (error: any) {
    const errorMsg = error?.message || (typeof error === 'string' ? error : 'Unknown error')
    notificationStore.showError('Failed to save farm: ' + errorMsg)
  }
}

const sizePlaceholder = computed(() => {
  if (!form.size_unit) return 'Select unit first'
  if (form.size_unit === 'DIMENSIONS') return 'e.g. 10x20'
  return 'Enter size value'
})

const displayUnit = computed(() => {
  const units: Record<string, string> = {
    ACRES: 'Acres',
    HECTARES: 'Hectares',
    SQUARE_METRES: 'Square Metres',
    DIMENSIONS: 'Width × Length',
  }
  return units[form.size_unit] || ''
})
</script>
